// Gedeelde formatteer-helpers voor tabellen, dashboard, CSV en print.

const eurFmt = new Intl.NumberFormat("nl-NL", {
  style: "currency",
  currency: "EUR",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/** Formatteer een getal als euro (nl-NL). Accepteert Prisma-Decimal-strings. */
export function eur(n: number | string | null | undefined): string {
  return eurFmt.format(Number(n ?? 0) || 0);
}

// Multi-valuta: per-valuta Intl.NumberFormat cache (nl-NL-stijl). EUR gebruikt
// de gedeelde eurFmt hierboven; andere ISO-valuta's krijgen een eigen instance.
const currencyFmtCache = new Map<string, Intl.NumberFormat>();

/** Formatteer een bedrag in een willekeurige ISO-4217 valuta (nl-NL-stijl).
 *  Default EUR — `fmtCurrency(n)` is equivalent aan `eur(n)`. Accepteert
 *  Prisma-Decimal-strings en null/undefined (→ 0). */
export function fmtCurrency(n: number | string | null | undefined, currency: string = "EUR"): string {
  const cur = (currency || "EUR").toUpperCase();
  if (cur === "EUR") return eurFmt.format(Number(n ?? 0) || 0);
  let f = currencyFmtCache.get(cur);
  if (!f) {
    f = new Intl.NumberFormat("nl-NL", { style: "currency", currency: cur, minimumFractionDigits: 2, maximumFractionDigits: 2 });
    currencyFmtCache.set(cur, f);
  }
  return f.format(Number(n ?? 0) || 0);
}

/** Datum kort (nl-NL), of "—" bij ontbreken. */
export function fmtDate(d: string | Date | null | undefined): string {
  if (!d) return "—";
  const dt = typeof d === "string" ? new Date(d) : d;
  if (isNaN(dt.getTime())) return "—";
  return dt.toLocaleDateString("nl-NL");
}

/** Tijd HH:mm (nl-NL), of "—" bij ontbreken. */
export function fmtTime(d: string | Date | null | undefined): string {
  if (!d) return "—";
  const dt = typeof d === "string" ? new Date(d) : d;
  if (isNaN(dt.getTime())) return "—";
  return dt.toLocaleTimeString("nl-NL", { hour: "2-digit", minute: "2-digit" });
}

const STATUS_LABELS: Record<string, string> = {
  // Quote
  DRAFT: "Concept",
  SENT: "Verstuurd",
  ACCEPTED: "Geaccepteerd",
  REJECTED: "Afgewezen",
  INVOICED: "Gefactureerd",
  // Invoice
  PAID: "Betaald",
  OVERDUE: "Achterstallig",
  CREDIT: "Credit",
  // Payment method (niet-status, maar handig)
  BANK_TRANSFER: "Bankoverschrijving",
  CASH: "Contant",
  IDEAL: "iDEAL",
  CREDIT_CARD: "Creditcard",
  PAYPAL: "PayPal",
  OTHER: "Overige",
  // OCR status (uitgaven)
  PENDING: "In wachtrij",
  PROCESSING: "Bezig",
  DONE: "Verwerkt",
  FAILED: "Mislukt",
  SKIPPED: "Overgeslagen",
};

export function nlStatus(label: string | null | undefined): string {
  if (!label) return "—";
  return STATUS_LABELS[label] || label;
}

// ── Datum-groepering (maand / kwartaal / jaar) voor overzichtslijsten ──

export const MONTHS_NL = [
  "januari", "februari", "maart", "april", "mei", "juni",
  "juli", "augustus", "september", "oktober", "november", "december",
] as const;

export type GroupGranularity = "month" | "quarter" | "year";

/** Sleutel waarmee rijen per periode worden gebucket. Geldige sleutels sorteren
 *  lexicografisch aflopend op tijd (nieuwste eerst): "2026-08", "2026-Q3", "2026".
 *  Ontbrekende/ongeldige datum → "Zonder datum" (sorteert altijd onderaan). */
export function dateToGroupKey(
  d: string | number | Date | null | undefined,
  granularity: GroupGranularity
): string {
  if (d === null || d === undefined || d === "") return "Zonder datum";
  const dt = typeof d === "string" || typeof d === "number" ? new Date(d) : d;
  if (isNaN(dt.getTime())) return "Zonder datum";
  const y = dt.getFullYear();
  const m = dt.getMonth(); // 0-11
  if (granularity === "year") return String(y);
  if (granularity === "quarter") return `${y}-Q${Math.floor(m / 3) + 1}`;
  return `${y}-${String(m + 1).padStart(2, "0")}`; // month
}

/** Menselijk label bij een groepsleutel, bv. "augustus 2026" / "3e kwartaal 2026" / "2026". */
export function groupKeyLabel(key: string, granularity: GroupGranularity): string {
  if (key === "Zonder datum") return "Zonder datum";
  if (granularity === "year") return key;
  if (granularity === "quarter") {
    const [y, q] = key.split("-Q");
    const n = Number(q);
    return `${n}e kwartaal ${y}`;
  }
  // month "2026-08"
  const [y, mm] = key.split("-");
  const idx = Number(mm) - 1;
  return `${MONTHS_NL[idx] ?? mm} ${y}`;
}